// pages/Donate.jsx
import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  FaPhone, 
  FaCopy, 
  FaCheck, 
  FaHeart, 
  FaHandHoldingHeart, 
  FaArrowRight,
  FaEnvelope,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaUsers,
  FaLeaf,
  FaGavel,
  FaHeartbeat,
  FaLightbulb
} from "react-icons/fa";
import { motion } from "framer-motion";

function Donate() {
  const [copied, setCopied] = useState(false);
  const phoneNumber = "+254140014842";

  const copyToClipboard = () => {
    navigator.clipboard.writeText(phoneNumber.replace(/\s/g, ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const impactAreas = [
    { icon: FaHeartbeat, label: "Health & Nutrition", color: "text-red-500" },
    { icon: FaLeaf, label: "Climate Justice", color: "text-green-500" },
    { icon: FaGavel, label: "Gender Equality", color: "text-pink-500" },
    { icon: FaLightbulb, label: "Economic Empowerment", color: "text-yellow-500" }
  ];

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-primaryBlue text-white py-16 md:py-24 overflow-hidden">
        {/* Animated Background */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute top-0 left-0 w-64 h-64 bg-primaryGold rounded-full -translate-x-1/2 -translate-y-1/2"
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 1.5, delay: 0.3 }}
          className="absolute bottom-0 right-0 w-96 h-96 bg-primaryGold rounded-full translate-x-1/3 translate-y-1/3"
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center"
          >
            <motion.div
              variants={fadeInUp}
              className="inline-block p-3 md:p-4 bg-primaryGold/20 rounded-full mb-4 md:mb-6"
            >
              <FaHeart className="text-2xl md:text-4xl text-primaryGold" />
            </motion.div>
            <motion.h1 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 px-4"
            >
              Support <span className="text-primaryGold">Our Mission</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-base sm:text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-gray-100 px-4"
            >
              Your contribution helps us empower women, youth, and marginalized communities in Kakamega 
              through gender justice, climate action, and sustainable development.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Donate Section */}
      <section className="py-16 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
            className="bg-white rounded-2xl md:rounded-3xl shadow-xl md:shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primaryGold to-yellow-400 px-6 md:px-8 py-6 md:py-8">
              <h2 className="text-2xl md:text-3xl font-bold text-primaryBlue text-center">
                Make a Donation
              </h2>
              <p className="text-primaryBlue/80 text-center mt-2 text-sm md:text-base">
                Your support transforms lives
              </p>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8">
              {/* Phone Number Card */}
              <motion.div 
                variants={fadeInUp}
                className="bg-gradient-to-br from-gray-50 to-white rounded-xl md:rounded-2xl p-6 md:p-8 border-2 border-primaryGold/20 mb-8"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-primaryGold/10 rounded-full flex items-center justify-center">
                    <FaPhone className="text-xl md:text-2xl text-primaryGold" />
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold text-primaryBlue">M-Pesa Paybill</h3>
                    <p className="text-sm md:text-base text-gray-600">Copy the number below to donate via M-Pesa</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  {/* Phone Number Display */}
                  <div className="flex-1 bg-white border-2 border-gray-200 rounded-xl p-4 md:p-5 font-mono text-lg md:text-2xl font-bold text-primaryBlue text-center sm:text-left">
                    {phoneNumber}
                  </div>

                  {/* Copy Button */}
                  <button
                    onClick={copyToClipboard}
                    className={`flex items-center justify-center gap-2 px-6 md:px-8 py-4 md:py-5 rounded-xl font-semibold transition-all duration-300 ${
                      copied 
                        ? 'bg-green-500 text-white' 
                        : 'bg-primaryGold text-primaryBlue hover:bg-yellow-400'
                    }`}
                  >
                    {copied ? (
                      <>
                        <FaCheck /> Copied!
                      </>
                    ) : (
                      <>
                        <FaCopy /> Copy Number
                      </>
                    )}
                  </button>
                </div>

                {/* Instructions */}
                <div className="mt-6 p-4 bg-primaryBlue/5 rounded-lg">
                  <h4 className="font-semibold text-primaryBlue mb-2 text-sm md:text-base">How to donate via M-Pesa:</h4>
                  <ol className="list-decimal list-inside space-y-1 text-xs md:text-sm text-gray-600">
                    <li>Go to M-Pesa on your phone</li>
                    <li>SelectSend Money"</li>
                    <li>Enter Phone Number: <span className="font-mono font-bold">{phoneNumber}</span></li>
                    <li>Enter the amount you wish to donate</li>
                    <li>Enter your M-Pesa PIN and confirm</li>
                  </ol>
                </div>
              </motion.div>

              {/* Alternative Payment Methods */}
              <motion.div 
                variants={fadeInUp}
                className="grid md:grid-cols-2 gap-6 mt-8"
              >

                {/* Contact for Other Methods */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-primaryBlue mb-4">Other Ways to Give</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    For international donations, corporate partnerships, or other payment methods, please contact us:
                  </p>
                  <div className="space-y-2">
                    <p className="flex items-center gap-2 text-sm">
                      <FaEnvelope className="text-primaryGold" />
                      <a href="mailto:info@risingtogreatness.org" className="hover:text-primaryGold transition-colors">
                        info@risingtogreatness.org
                      </a>
                    </p>
                    <p className="flex items-center gap-2 text-sm">
                      <FaPhone className="text-primaryGold" />
                      <a href="tel:+254140014842" className="hover:text-primaryGold transition-colors">
                        +254140014842
                      </a>
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Impact Preview */}
              <motion.div 
                variants={fadeInUp}
                className="mt-8 p-6 bg-primaryBlue/5 rounded-xl"
              >
                <h3 className="text-lg font-semibold text-primaryBlue mb-4">Your Donation Makes an Impact</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {impactAreas.map((area, index) => (
                    <div key={index} className="text-center">
                      <area.icon className={`text-2xl md:text-3xl ${area.color} mx-auto mb-2`} />
                      <p className="text-xs md:text-sm font-medium text-gray-700">{area.label}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Donate Section */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
            className="text-center mb-10 md:mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-primaryBlue mb-4"
            >
              Why Donate to <span className="text-primaryGold">Rising to Greatness</span>
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto"
            >
              Your support helps us create lasting change in communities across Kakamega
            </motion.p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="grid md:grid-cols-3 gap-6 md:gap-8"
          >
            {[
              {
                icon: FaUsers,
                title: "Community-Led",
                description: "We work directly with communities to identify needs and implement sustainable solutions."
              },
              {
                icon: FaShieldAlt,
                title: "Transparent",
                description: "100% of donations are tracked and reported. We maintain complete financial transparency."
              },
              {
                icon: FaHandHoldingHeart,
                title: "Proven Impact",
                description: "Over 5,000 beneficiaries reached through our programs in Western Kenya."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-gray-50 rounded-xl md:rounded-2xl p-6 md:p-8 text-center hover:shadow-xl transition-all duration-300 group"
              >
                <div className="w-16 h-16 md:w-20 md:h-20 bg-primaryGold/10 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                  <item.icon className="text-2xl md:text-3xl text-primaryGold" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-primaryBlue mb-2 md:mb-3">{item.title}</h3>
                <p className="text-sm md:text-base text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-r from-primaryBlue to-primaryGold">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6"
            >
              Every Contribution Counts
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-base md:text-lg text-white/90 mb-6 md:mb-8 max-w-2xl mx-auto"
            >
              No matter the size, your donation helps us empower communities and create sustainable change.
            </motion.p>
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/contact"
                className="bg-white text-primaryBlue px-8 py-3 md:px-10 md:py-4 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Contact Us
              </Link>
              <Link
                to="/programs"
                className="bg-transparent border-2 border-white text-white px-8 py-3 md:px-10 md:py-4 rounded-full font-semibold hover:bg-white/10 transition-all duration-300"
              >
                Learn About Our Programs
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Donate;